import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { graphql, Link } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';

import { useTextBackgroundData } from './hooks';

import { RichText } from 'components/Prismic';
import { ANIMATION_COMPLETE_CLASS } from 'utils/animations/useBounceTextEnterAnimation';
import { MASK_VARIABLES } from 'utils/animations/useMaskCircle';
import { filterProps } from 'utils/helpers';
import { Normalize } from 'utils/types';

export type TCircularExpansionIndustriesSection =
  Normalize<Queries.SolutionDesktopCircularExpansionIndustriesData_PrismicSolutionDataDesktopSolutionCircularExpansionIndustries_Fragment>;

enum CIRCULAR_EXPANSION_INDUSTRIES_VARIABLES {
  circleRadius = '--circle-radius',
  maskColor = '--maskColor',
}

const Container = styled.div`
  ${({ theme: { mq, spacings, utils } }) => css`
    ${CIRCULAR_EXPANSION_INDUSTRIES_VARIABLES.circleRadius}: ${utils.rem(432)};

    display: grid;
    position: relative;
    row-gap: 8rem;
    margin-bottom: -1px;
    padding: 13.25rem 0;
    width: 100%;
    overflow: hidden;

    ${mq.md} {
      ${CIRCULAR_EXPANSION_INDUSTRIES_VARIABLES.circleRadius}: ${utils.rem(112)};

      padding: 10.5rem ${spacings.padding} 14.5rem;
      width: initial;

      && {
        display: grid;
      }
    }
  `}
`;

const BackgroundImage = styled(GatsbyImage)`
  position: absolute;
  width: 100%;
  height: 100%;
`;

const ContentContainer = styled.div`
  ${({ theme: { mq } }) => css`
    display: grid;
    position: relative;
    grid-template-columns: 1fr 1fr;
    grid-row: 1 / -1;
    grid-column: 1 / -1;
    grid-auto-flow: column;
    align-content: center;
    align-items: center;
    align-self: center;
    z-index: 1;
    height: calc(var(${CIRCULAR_EXPANSION_INDUSTRIES_VARIABLES.circleRadius}) * 2);

    ${mq.md} {
      grid-template-columns: 1fr;
    }
  `}
`;

const TitleContainer = styled.div`
  display: grid;

  ${({ theme: { mq } }) => css`
    ${mq.md} {
      & > div:first-of-type {
        margin-left: -1.5rem;
      }
    }
  `}
`;

const TitleLineWrapper = styled('div', filterProps('isMobile'))<{
  isMobile?: boolean;
}>`
  ${({ theme: { colors, mq }, isMobile = false }) => css`
    display: ${isMobile ? 'none' : 'grid'};
    justify-self: flex-end;
    margin-right: 25.625rem;
    width: fit-content;

    &.${ANIMATION_COMPLETE_CLASS} {
      ${CIRCULAR_EXPANSION_INDUSTRIES_VARIABLES.maskColor}: ${colors.primary};
    }

    ${mq.md} {
      ${CIRCULAR_EXPANSION_INDUSTRIES_VARIABLES.maskColor}: ${colors.primary};

      display: ${isMobile ? 'grid' : 'none'};
      justify-self: center;
      transform: none !important;
      margin-right: 0;
    }
  `}
`;

const Title = styled(RichText)`
  ${({ theme: { colors, mq, tp } }) => css`
    display: flex;
    flex-direction: column;
    grid-row: 1 / span 1;
    grid-column: 1 / span 1;

    & > h2 {
      margin: 0 -2rem -2rem 0;
      padding: 0 2rem 2rem 0;
      text-align: right;
      line-height: 0.76;
      letter-spacing: -0.01em;
      color: ${colors.black};
      font-size: 23.5rem;
      -webkit-text-stroke: 1px ${colors.black};

      ${tp.useFont(fonts => fonts.Switzer.regular)};
    }

    ${mq.md} {
      & > h2 {
        text-align: center;
        letter-spacing: -0.11em;
        font-size: 5.5rem;

        ${tp.useFont(fonts => fonts.Switzer.lightest)};

        strong {
          text-align: center;
          line-height: 0.965;
          letter-spacing: -0.11em;
          font-size: 15.625rem;
          ${tp.useFont(fonts => fonts.Switzer.regular)};
        }
      }
    }
  `}
`;

const TitleMask = styled(Title)<{
  alignRight?: boolean;
}>`
  ${({ theme: { durations } }) => css`
    & > h2 {
      transition: background-color ${durations.long};
      background-clip: text;
      background-color: var(${CIRCULAR_EXPANSION_INDUSTRIES_VARIABLES.maskColor});
      clip-path: circle(
        calc(
            var(${CIRCULAR_EXPANSION_INDUSTRIES_VARIABLES.circleRadius}) *
              var(${MASK_VARIABLES.CIRCLE_SCALE})
          )
          at var(${MASK_VARIABLES.TEXT_MASK_X}) var(${MASK_VARIABLES.TEXT_MASK_Y})
      );
      -webkit-text-stroke: 1px transparent;
      -webkit-text-fill-color: transparent;
    }
  `}
`;

const Circle = styled.div`
  ${({ theme: { colors } }) => css`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scale(var(${MASK_VARIABLES.CIRCLE_SCALE}));
    border-radius: 50%;
    background-color: ${colors.white}1A;
    height: calc(var(${CIRCULAR_EXPANSION_INDUSTRIES_VARIABLES.circleRadius}) * 2);
    aspect-ratio: 1 / 1;
  `}
`;

const IndustriesContainer = styled.div`
  ${({ theme: { mq } }) => css`
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-row: 1 / -1;
    grid-column: 1 / -1;
    grid-auto-flow: column;
    align-content: center;
    align-items: center;
    align-self: center;
    z-index: 1;

    ${mq.md} {
      grid-template-columns: 1fr;
      grid-row: 2 / span 1;
    }
  `}
`;

const IndustryLinks = styled.div`
  ${({ theme: { mq } }) => css`
    display: flex;
    flex-direction: column;
    grid-row: 1 / span 1;
    grid-column: 2 / span 1;
    row-gap: 2rem;
    z-index: 1;
    margin-left: -16rem;
    background-color: #2c2c2c40;
    padding: 3.25rem 0 3.25rem 4.25rem;

    ${mq.md} {
      grid-column: 1 / span 1;
      margin-left: 0;
      background-color: initial;
      padding: 0;
    }
  `}
`;

const IndustryLink = styled(Link)`
  ${({ theme: { colors, mq, tp } }) => css`
    text-decoration: none;
    line-height: 1.04;
    color: ${colors.white};
    font-size: 5rem;

    & span {
      padding: 0 2rem;
      color: ${colors.primary};
      font-weight: bold;
    }

    ${tp.useFont(fonts => fonts.Switzer.lightest)};

    ${mq.md} {
      font-size: 2rem;

      & span {
        padding: 0 0 0 1rem;
      }
    }
  `}
`;

const CircularExpansionIndustriesSection: React.FC<
  TCircularExpansionIndustriesSection
> = sliceData => {
  const {
    containerRef,
    contentRef,
    getTitleRef,
    backgroundImage,
    title,
    mobileTitle,
    industryLinks,
  } = useTextBackgroundData(sliceData);

  return (
    <Container ref={containerRef}>
      {backgroundImage ? <BackgroundImage {...backgroundImage} /> : null}
      <ContentContainer ref={contentRef}>
        <TitleContainer>
          {mobileTitle.map(titleText => {
            const key = JSON.stringify(titleText);

            return (
              <TitleLineWrapper key={key} isMobile ref={getTitleRef(key)}>
                <Title render={titleText} />
                <TitleMask render={titleText} />
              </TitleLineWrapper>
            );
          })}
          {title.map(titleText => {
            const key = JSON.stringify(titleText);

            return (
              <TitleLineWrapper key={key} ref={getTitleRef(key)}>
                <Title render={titleText} />
                <TitleMask render={titleText} />
              </TitleLineWrapper>
            );
          })}
        </TitleContainer>
        <Circle />
      </ContentContainer>

      <IndustriesContainer>
        <IndustryLinks>
          {industryLinks.map(({ label, url }) => (
            <IndustryLink key={`${label}${url}`} to={url}>
              {label}
              <span>→</span>
            </IndustryLink>
          ))}
        </IndustryLinks>
      </IndustriesContainer>
    </Container>
  );
};

export const desktopCircularExpansionIndustriesFragment = graphql`
  fragment SolutionDesktopCircularExpansionIndustriesData on PrismicSolutionDataDesktopSlicesType {
    ... on PrismicSolutionDataDesktopSolutionCircularExpansionIndustries {
      slice_type
      primary {
        solution_circular_expansion_industries_image {
          gatsbyImageData
          alt
        }
        solution_circular_expansion_industries_title {
          richText
        }
        solution_circular_expansion_industries_mobile_title {
          richText
        }
      }
      items {
        solution_circular_expansion_industry_label
        solution_circular_expansion_industry_link {
          url
        }
      }
    }
  }
`;

export default CircularExpansionIndustriesSection;
